export interface AddDsoLineItemParams {
  spreeVariantId: string;
  quantity: number;
  shipAccountNumber: string;
}

export interface UpdateDsoLineItemParams {
  childLineItemId: number;
  quantity: number;
  shipAccountNumber: string;
}

export interface DsoCart {
  orderId: number;
  orderNumber: string;
  state: string;
  currency: string;
  lineItems: DsoLineItem[];
  displayOrderSubTotal: string;
  displayOrderAdjustmentTotal: string;
  displayOrderNetTotal: string;
  displayOrderShippingTotal: string;
  displayOrderTaxTotal: string;
  displayOrderGrandTotal: string;
  displayOrderDiscountTotal: string;
  displayOrderSavingsTotal: string;
  billingAddress: string | null;
  billAccountNumber: string | null;
  shippingMethod: string;
  paymentMethod: string;
  isMultiShipOrder: boolean;
  lineItemSavings: string;
  shipmentSavings: string;
  orderSavings: string;
  itemCount: number;
  promotions: DsoPromotion[];
}

export interface DsoLineItem {
  lineItemId: number;
  name: string;
  sku: string;
  imageUrl: string;
  inStock: boolean;
  slug: string;
  quantityLimit: number[];
  displayUnitPrice: string;
  spreeVariantId: number;
  quantity: number;
  displaySubTotal: string;
  discountPercentage: string;
  displayAdjustmentTotal: string;
  displayNetTotal: string;
  children: DsoChildLineItem[];
}

export interface DsoChildLineItem {
  childLineItemId: number;
  quantity: number;
  shipAccountNumber: string;
  displayUnitPrice: string;
  displaySubTotal: string;
  displayAdjustmentTotal: string;
  displayNetTotal: string;
  discountPercentage: string;
  displayDiscountTotal: string;
}

export interface DsoPromotion {
  promoCode: string;
  promoTotal: string;
}

export interface DsoCartResponse {
  data: {
    order_id: number;
    order_number: string;
    state: string;
    currency: string;
    bill_address: string | null;
    display_order_sub_total: string;
    display_order_adjustment_total: string;
    display_order_net_total: string;
    display_order_shipping_total: string;
    display_order_tax_total: string;
    display_order_grand_total: string;
    display_order_discount_total: string;
    display_order_savings_total: string;
    line_items: DsoLineItemResponse[];
    billing_address: {
      bill_account_number: string;
      bill_address: string | null;
    };
    shipping_method: {
      id: string | null;
      label: string;
    };
    payment_method: {
      id: string | null;
      label: string;
    };
    is_multi_ship_order: boolean;
  };
}

export interface DsoLineItemResponse {
  line_item_id: number;
  variant: {
    id: number;
    name: string;
    sku: string;
    image_url: string;
    in_stock: boolean;
    slug: string;
    display_unit_price: string;
    quantity_limit: number[];
  };
  quantity: number;
  display_sub_total: string;
  display_adjustment_total: string;
  display_net_total: string;
  discount_percentage: string;
  children: DsoChildLineItemResponse[];
}

export interface DsoChildLineItemResponse {
  child_line_item_id: number;
  quantity: number;
  ship_account_number: string;
  ship_address: string;
  display_unit_price: string;
  display_sub_total: string;
  display_adjustment_total: string;
  display_net_total: string;
  discount_percentage: string;
  display_discount_total: string;
}

export interface DsoPromotionResponse {
  code: string;
  display_amount: string;
}

export interface DsoCheckoutSummaryShipmentProduct {
  name: string;
  image_url: string;
  slug: string;
  spree_variant_id: number;
  display_unit_price: string;
  display_item_sub_total: string;
  quantity: number;
}

export interface DsoCheckoutSummaryShipment {
  ship_address: string;
  products: DsoCheckoutSummaryShipmentProduct[];
}

export interface DsoCheckoutSummaryResponse {
  data: {
    shipments: DsoCheckoutSummaryShipment[];
    display_order_sub_total: string;
    display_order_adjustment_total: string;
    display_order_net_total: string;
    display_order_shipping_total: string;
    display_order_tax_total: string;
    display_order_grand_total: string;
  };
}

export interface DsoCheckoutSummaryProduct {
  name: string;
  imageUrl: string;
  slug: string;
  spreeVariantId: number;
  displayUnitPrice: string;
  displayItemSubTotal: string;
  quantity: number;
}

export interface DsoCheckoutSummaryShipmentClient {
  accountName: string;
  shipAddress: string;
  products: DsoCheckoutSummaryProduct[];
}

export interface DsoCheckoutSummary {
  shipments: DsoCheckoutSummaryShipmentClient[];
  displayOrderSubTotal: string;
  displayOrderAdjustmentTotal: string;
  displayOrderNetTotal: string;
  displayOrderShippingTotal: string;
  displayOrderTaxTotal: string;
  displayOrderGrandTotal: string;
  displayOrderDiscountTotal: string;
  displayOrderSavingsTotal: string;
}

export interface DsoCheckoutSummaryShipAddress {
  shipAddress: string;
  quantity: number;
}

export interface DsoCheckoutSummaryShipmentByProduct {
  name: string;
  imageUrl: string;
  slug: string;
  spreeVariantId: number;
  displayItemSubTotal: string;
  shipAddresses: DsoCheckoutSummaryShipAddress[];
}

export interface DsoCheckoutSummaryByProduct {
  products: DsoCheckoutSummaryShipmentByProduct[];
  displayOrderSubTotal: string;
  displayOrderAdjustmentTotal: string;
  displayOrderNetTotal: string;
  displayOrderShippingTotal: string;
  displayOrderTaxTotal: string;
  displayOrderGrandTotal: string;
}

export interface DsoPermissionsResponse {
  permissions: string[];
}

// --- DSO Cart Validation Error Types ---

export enum DsoCartErrorCode {
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  INSUFFICIENT_STOCK = 'INSUFFICIENT_STOCK',
  TIME_LIMIT_EXCEEDED = 'TIME_LIMIT_EXCEEDED',
  MAX_PER_ORDER_EXCEEDED = 'MAX_PER_ORDER_EXCEEDED',
  MIN_PER_ORDER_NOT_MET = 'MIN_PER_ORDER_NOT_MET',
  INVALID_ORDER_MULTIPLE = 'INVALID_ORDER_MULTIPLE',
  PRODUCT_INACTIVE = 'PRODUCT_INACTIVE',
  PRODUCT_NOT_AVAILABLE_DSO = 'PRODUCT_NOT_AVAILABLE_DSO',
  PRICE_NOT_AVAILABLE = 'PRICE_NOT_AVAILABLE',
}

export type DsoTimeLimitPeriod =
  | '1Y'
  | 'half_year'
  | '1Q'
  | '1M'
  | 'year'
  | 'half-year'
  | 'quarter'
  | 'month';

interface DsoCartErrorBase {
  error_code: DsoCartErrorCode;
  product_name: string;
}

export interface DsoOutOfStockError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.OUT_OF_STOCK;
}

export interface DsoInsufficientStockError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.INSUFFICIENT_STOCK;
  available_quantity: number;
  requested_quantity: number;
}

export interface DsoTimeLimitExceededError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.TIME_LIMIT_EXCEEDED;
  limit_period: DsoTimeLimitPeriod;
  max_quantity: number;
  ordered_quantity: number;
  remaining: number;
  requested_quantity: number;
}

export interface DsoMaxPerOrderExceededError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.MAX_PER_ORDER_EXCEEDED;
  max_quantity_per_order: number;
  requested_quantity: number;
}

export interface DsoMinPerOrderNotMetError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.MIN_PER_ORDER_NOT_MET;
  min_quantity_per_order: number;
  requested_quantity: number;
}

export interface DsoInvalidOrderMultipleError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.INVALID_ORDER_MULTIPLE;
  order_multiple: number;
  requested_quantity: number;
}

export interface DsoProductInactiveError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.PRODUCT_INACTIVE;
}

export interface DsoProductNotAvailableDsoError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.PRODUCT_NOT_AVAILABLE_DSO;
}

export interface DsoPriceNotAvailableError extends DsoCartErrorBase {
  error_code: DsoCartErrorCode.PRICE_NOT_AVAILABLE;
  currency: string;
  country: string;
}

export type DsoCartValidationError =
  | DsoOutOfStockError
  | DsoInsufficientStockError
  | DsoTimeLimitExceededError
  | DsoMaxPerOrderExceededError
  | DsoMinPerOrderNotMetError
  | DsoInvalidOrderMultipleError
  | DsoProductInactiveError
  | DsoProductNotAvailableDsoError
  | DsoPriceNotAvailableError;

export type DsoCartErrorResponse =
  | { error: string }
  | DsoCartValidationError;
